import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { QuestionFormData, questionSchema } from '@/lib/questionSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFieldArray, useForm } from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, X } from 'lucide-react';
import { useEffect } from 'react';

export function QuestionForm({
  onSubmit,
  onCancel,
  isSubmitting,
  initialData,
}: {
  onSubmit: (data: QuestionFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  initialData?: Partial<QuestionFormData>;
}) {
  const form = useForm<QuestionFormData>({
    resolver: zodResolver(questionSchema),

    defaultValues: initialData || {
      key: '',
      label: '',
      question_type: 'string',
      required: true,
      placeholder: '',
      is_filterable: false,
      is_visible_to_mentors: true,
      filter_display_name: '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'options',
  });

  const questionType = form.watch('question_type');
  const showOptions = ['radio', 'select', 'multi_select'].includes(
    questionType
  );

  useEffect(() => {
    if (showOptions && fields.length === 0) {
      append({ label: '', value: '' });
    }
  }, [showOptions, fields.length, append]);

  useEffect(() => {
    if (!showOptions && fields.length > 0) {
      fields.forEach((_, index) => remove(index));
    }
  }, [showOptions, fields, remove]);
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="label"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Question Label</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g., What is your email address?"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                This is what the users will see when answering the question
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="key"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Question Key</FormLabel>
              <FormControl>
                <Input placeholder="e.g., email_address" {...field} />
              </FormControl>
              <FormDescription>
                Unique identifier (lowercase, numbers, underscores only)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="placeholder"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Placeholder</FormLabel>
              <FormControl>
                <Input placeholder="e.g., john@project-short.com" {...field} />
              </FormControl>
              <FormDescription>
                This provides an example answer to the user
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="question_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Question Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a question type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="string">Short Text</SelectItem>
                  <SelectItem value="text">Long Text</SelectItem>
                  <SelectItem value="radio">Radio Button</SelectItem>
                  <SelectItem value="select">Select Dropdown</SelectItem>
                  <SelectItem value="multi_select">Multi-Select</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                Choose how users will answer this question
              </FormDescription>
            </FormItem>
          )}
        />
        {questionType === 'multi_select' && (
          <FormField
            control={form.control}
            name="max_selections"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Maximum Selections (optional)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    placeholder="Leave blank for unlimited selections"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? parseInt(e.target.value) : null
                      )
                    }
                  />
                </FormControl>
                <FormDescription>
                  Limit how many options users can select
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="required"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <label>Required Question</label>
                <FormDescription>
                  Users must answer this question to continue
                </FormDescription>
              </div>
            </FormItem>
          )}
        />

        <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
          <h3 className="text-sm font-semibold">Filter Configuration</h3>
          <FormField
            control={form.control}
            name="is_filterable"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                    disabled={questionType === 'text'}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <label>Use as Filter</label>
                  <FormDescription>
                    Allow mentors to filter mentees by this question{' '}
                    {questionType === 'text' &&
                      '(Not available for text fields)'}
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          {form.watch('is_filterable') && (
            <FormField
              control={form.control}
              name="filter_display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Filter Display Name (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., 'School' instead of 'What undergraduate institution did you attend?'"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormDescription>
                    Short label for the filter. Defaults to question label if
                    not provided.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="is_visible_to_mentors"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <label>Visible to Mentors</label>
                  <FormDescription>
                    Mentors can see answers to this question when browsing
                    mentees
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="order"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Display Order</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="e.g., 1"
                  min={1}
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? parseInt(e.target.value) : null
                    )
                  }
                />
              </FormControl>
              <FormDescription>
                The order in which this question appears (lower numbers appear
                first)
              </FormDescription>
            </FormItem>
          )}
        />

        {showOptions && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Answer Options</div>
              <Button
                type="button"
                variant={'outline'}
                size={'sm'}
                onClick={() => append({ label: '', value: '' })}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Option
              </Button>
            </div>

            <div className="space-y-3">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex gap-2 items-start p-4 border rounded-lg"
                >
                  <div className="flex-1 space-y-3">
                    <FormField
                      control={form.control}
                      name={`options.${index}.label`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Option Text</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g., Computer Science"
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`options.${index}.value`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Option Value</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., comp_sci" {...field} />
                          </FormControl>
                          <FormDescription>
                            Unique identifier (lowercase, numbers, underscores
                            only)
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(index)}
                    disabled={fields.length === 1 && showOptions}
                    className="mt-6"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {form.formState.errors.options && (
                <p className="text-sm text-red-500">
                  {form.formState.errors.options.message}
                </p>
              )}
            </div>
            {fields.length === 0 && showOptions && (
              <div className="text-center p-4 border border-dashed rounded-lg">
                <p className="text-sm text-gray-500 mb-2">
                  No options added yet
                </p>
                <Button
                  type="button"
                  variant={'outline'}
                  onClick={() => append({ label: '', value: '' })}
                >
                  Add First Option
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-6 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Question'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
